import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import Footer from '@/components/Footer';
import StoryViewer from '@/components/StoryViewer';
import LessonChat from '@/components/LessonChat';
import InteractiveWorksheet from '@/components/InteractiveWorksheet';
import { LessonTextContent } from '@/components/lesson-text/LessonTextContent';
import { escapePrintText, lessonReadingPrintStyles } from '@/components/lesson-text/lessonReadingPrint';
import '@/components/lesson-text/LessonDialog.css';
import { DidTutorWidget } from '@/components/DidTutorDialog';
import { 
  FileText, 
  ArrowRight, 
  Lightbulb, 
  ClipboardList, 
  HelpCircle, 
  BookOpen,
  Download,
  Loader2,
  CheckCircle,
  XCircle,
  Printer,
  LogOut,
  GraduationCap,
  Camera,
  BookOpenCheck,
  MessageCircle,
  ExternalLink,
  MonitorPlay,
  User,
  BarChart3
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LessonDetailPage = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(null);
  const [aiContent, setAiContent] = useState(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [quizState, setQuizState] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [storyData, setStoryData] = useState(null);
  const [storyViewerOpen, setStoryViewerOpen] = useState(false);
  const [storyLoading, setStoryLoading] = useState(false);
  const [cachedStoryExists, setCachedStoryExists] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [toolFilter, setToolFilter] = useState('all');
  const [cachedGuideExists, setCachedGuideExists] = useState(false);
  const [cachedExplainExists, setCachedExplainExists] = useState(false);
  const [cachedTypes, setCachedTypes] = useState([]);
  const [completedTools, setCompletedTools] = useState([]);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [didTutorOpen, setDidTutorOpen] = useState(false);
  const [teacherVideos, setTeacherVideos] = useState([]);
  const [teacherVideoDialogOpen, setTeacherVideoDialogOpen] = useState(null);
  const contentRef = useRef(null);
  const imageInputRef = useRef(null);
  const { user, logout, getAuthHeaders } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/lessons/${lessonId}`, {
          headers: getAuthHeaders()
        });
        setLesson(response.data);

        // Fire all cache checks in parallel (lightweight calls)
        const promises = [
          axios.get(`${API_URL}/api/ai-cache/${lessonId}`).catch(() => null),
          axios.get(`${API_URL}/api/teach-guides/${lessonId}`).catch(() => null),
          axios.get(`${API_URL}/api/progress/${lessonId}`, { headers: getAuthHeaders() }).catch(() => null),
        ];
        
        // Only check story existence for grades 1-4 (lightweight check)
        if (response.data.grade_number && response.data.grade_number <= 4) {
          promises.push(
            axios.get(`${API_URL}/api/stories/${lessonId}?check_only=true`).catch(() => null)
          );
        }

        const [cacheRes, guideRes, progressRes, storyRes] = await Promise.all(promises);

        // Fetch teacher videos for this lesson
        axios.get(`${API_URL}/api/lessons/${lessonId}/teacher-videos`, { headers: getAuthHeaders() })
          .then(r => setTeacherVideos(r.data || []))
          .catch(() => {});

        if (cacheRes?.data?.cached_types) {
          setCachedTypes(cacheRes.data.cached_types);
          if (cacheRes.data.cached_types.includes('teach-guide')) {
            setCachedGuideExists(true);
          }
          if (cacheRes.data.cached_types.includes('explain_lesson')) {
            setCachedExplainExists(true);
          }
        }
        if (guideRes?.data?.exists) {
          setCachedGuideExists(true);
        }
        if (storyRes?.data?.exists) {
          setCachedStoryExists(true);
        }
        if (progressRes?.data?.completed_tools) {
          setCompletedTools(progressRes.data.completed_tools);
        }
      } catch (error) {
        toast.error('فشل في تحميل الدرس');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchLesson();
  }, [lessonId, getAuthHeaders]);

  const isCached = (type) => cachedTypes.includes(type);

  const aiFeatures = [
    { 
      id: 'teach-guide', 
      title: cachedGuideExists ? 'دليل تدريس الدرس (جاهز)' : 'دليل تدريس الدرس للوالدين', 
      icon: <GraduationCap className="w-6 h-6" />,
      endpoint: '/ai/teach-guide',
      color: cachedGuideExists ? 'bg-emerald-500/20 text-emerald-400 hover:border-emerald-400/50' : 'bg-cyan-500/20 text-cyan-400 hover:border-cyan-400/50'
    },
    { 
      id: 'tips', 
      title: isCached('learning_tips') ? 'تدريس محتوى الدرس (جاهز)' : 'تدريس محتوى الدرس بشكل عام', 
      icon: <Lightbulb className="w-6 h-6" />,
      endpoint: '/ai/learning-tips',
      color: isCached('learning_tips') ? 'bg-emerald-500/20 text-emerald-400 hover:border-emerald-400/50' : 'bg-amber-500/20 text-amber-400 hover:border-amber-400/50'
    },
    { 
      id: 'worksheet', 
      title: 'ورقة عمل', 
      icon: <ClipboardList className="w-6 h-6" />,
      endpoint: '/ai/worksheet',
      color: 'bg-green-500/20 text-green-400 hover:border-green-400/50'
    },
    { 
      id: 'quiz', 
      title: 'اختبار قصير', 
      icon: <HelpCircle className="w-6 h-6" />,
      endpoint: '/ai/quiz',
      color: 'bg-blue-500/20 text-blue-400 hover:border-blue-400/50'
    },
    { 
      id: 'terms', 
      title: isCached('terms') ? 'المصطلحات المهمة (جاهز)' : 'المصطلحات المهمة', 
      icon: <BookOpen className="w-6 h-6" />,
      endpoint: '/ai/terms',
      color: isCached('terms') ? 'bg-emerald-500/20 text-emerald-400 hover:border-emerald-400/50' : 'bg-purple-500/20 text-purple-400 hover:border-purple-400/50'
    },
    { 
      id: 'help-solve', 
      title: 'ساعدني في حل سؤال', 
      icon: <Camera className="w-6 h-6" />,
      endpoint: '/ai/help-solve',
      color: 'bg-rose-500/20 text-rose-400 hover:border-rose-400/50',
      requiresImage: true
    },
    { 
      id: 'explain-lesson', 
      title: cachedExplainExists ? 'شرح الدرس للطالب (جاهز)' : 'شرح الدرس للطالب', 
      icon: <BookOpenCheck className="w-6 h-6" />,
      endpoint: '/ai/explain-lesson',
      color: cachedExplainExists ? 'bg-emerald-500/20 text-emerald-400 hover:border-emerald-400/50' : 'bg-indigo-500/20 text-indigo-400 hover:border-indigo-400/50'
    },
    { 
      id: 'private-tutor', 
      title: 'مدرس خصوصي ذكي (قريباً)', 
      icon: <GraduationCap className="w-6 h-6" />,
      endpoint: '',
      color: 'bg-gray-500/20 text-gray-400',
      comingSoon: true
    },
  ];

  // Story feature - only for grades 1-4
  const gradeNumber = lesson?.grade_number;
  const showStoryFeature = gradeNumber && gradeNumber <= 4;
  const showDidTutor = gradeNumber === 2 && lesson?.semester === 2 && lesson?.subject_name_en === 'English Language';

  const handleStoryGenerate = async () => {
    if (storyLoading || aiLoading) return;

    // If cached story exists, load full data if needed then open viewer
    if (cachedStoryExists && storyData) {
      setStoryViewerOpen(true);
      return;
    }

    // If story exists in cache but not loaded yet, fetch full data
    if (cachedStoryExists && !storyData) {
      setStoryLoading(true);
      try {
        const storyRes = await axios.get(`${API_URL}/api/stories/${lessonId}`);
        if (storyRes.data?.story) {
          setStoryData(storyRes.data.story);
          setStoryViewerOpen(true);
        }
      } catch (_) {
        toast.error('فشل في تحميل القصة');
      } finally {
        setStoryLoading(false);
      }
      return;
    }

    setStoryLoading(true);
    setStoryData(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/ai/lesson-story`,
        { lesson_id: lessonId },
        { headers: getAuthHeaders(), timeout: 300000 }
      );

      if (response.data?.story) {
        setStoryData(response.data.story);
        setCachedStoryExists(true);
        setStoryViewerOpen(true);
        toast.success('تم تأليف القصة بنجاح!');
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'فشل في تأليف القصة، حاول مرة أخرى');
    } finally {
      setStoryLoading(false);
    }
  };

  const handleAiFeature = async (feature) => {
    if (aiLoading) return;
    
    // If feature requires image, open upload dialog
    if (feature.requiresImage) {
      setImageUploadOpen(true);
      return;
    }
    
    setAiLoading(feature.id);
    setAiContent(null);
    setQuizState(null);
    setAiDialogOpen(false);
    
    // Show loading toast for explain-lesson (takes longer)
    if (feature.id === 'explain-lesson') {
      toast.loading('جارٍ شرح الدرس... قد يستغرق هذا حوالي دقيقة', { id: 'explain-loading', duration: 90000 });
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/api${feature.endpoint}`,
        {
          lesson_id: lessonId,
          lesson_title: lesson.title_ar,
          lesson_content: lesson.content_ar,
          request_type: feature.id
        },
        { headers: getAuthHeaders(), timeout: 120000 }
      );
      
      const content = {
        type: feature.id,
        title: feature.title,
        data: response.data
      };
      
      setAiContent(content);
      
      if (feature.id === 'quiz' && response.data.format === 'json' && response.data.content?.questions) {
        setQuizState({
          answers: {},
          submitted: false
        });
      }

      // Mark as cached after first generation
      if (feature.id === 'teach-guide') {
        setCachedGuideExists(true);
      }
      if (feature.id === 'explain-lesson') {
        setCachedExplainExists(true);
        setCachedTypes(prev => [...prev, 'explain_lesson']);
      }
      if (feature.id === 'tips') {
        setCachedTypes(prev => [...prev, 'learning_tips']);
      }
      if (feature.id === 'terms') {
        setCachedTypes(prev => [...prev, 'terms']);
      }
      
      setTimeout(() => {
        setAiDialogOpen(true);
      }, 100);
      
      toast.success('تم توليد المحتوى بنجاح!');
      toast.dismiss('explain-loading');
    } catch (error) {
      toast.dismiss('explain-loading');
      toast.error('فشل في توليد المحتوى');
      console.error(error);
    } finally {
      setAiLoading(null);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleImageSolve = async () => {
    if (!selectedImage || aiLoading) return;
    
    setAiLoading('help-solve');
    setImageUploadOpen(false);
    
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result.split(',')[1];
        
        try {
          const response = await axios.post(
            `${API_URL}/api/ai/help-solve`,
            {
              image_base64: base64,
              lesson_title: lesson.title_ar
            },
            { headers: getAuthHeaders() }
          );
          
          setAiContent({
            type: 'help-solve',
            title: 'ساعدني في حل سؤال',
            data: { content: response.data.solution }
          });
          
          setTimeout(() => setAiDialogOpen(true), 100);
          toast.success('تم تحليل السؤال بنجاح!');
        } catch (error) {
          toast.error('فشل في تحليل السؤال');
          console.error(error);
        } finally {
          setAiLoading(null);
          setSelectedImage(null);
          setImagePreview(null);
        }
      };
      reader.readAsDataURL(selectedImage);
    } catch (error) {
      toast.error('فشل في قراءة الصورة');
      setAiLoading(null);
    }
  };

  const handleQuizAnswer = (questionId, answerIndex) => {
    if (quizState?.submitted) return;
    
    setQuizState(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: answerIndex
      }
    }));
  };

  const submitQuiz = () => {
    setQuizState(prev => ({
      ...prev,
      submitted: true
    }));
    const questions = aiContent?.data?.content?.questions || [];
    const total = questions.length;
    let correct = 0;
    const wrongQuestions = [];
    questions.forEach(q => {
      if (quizState?.answers[q.id] === q.correct_answer) {
        correct++;
      } else {
        const studentIdx = quizState?.answers[q.id];
        wrongQuestions.push({
          question: q.question,
          correct_answer: q.options[q.correct_answer],
          student_answer: studentIdx !== undefined ? q.options[studentIdx] : 'لم يجب'
        });
      }
    });
    axios.post(`${API_URL}/api/progress/score`, {
      lesson_id: lessonId,
      lesson_title: lesson?.title_ar || '',
      tool_type: 'quiz',
      score: correct,
      total,
      wrong_questions: wrongQuestions
    }, { headers: getAuthHeaders() }).catch(() => {});
  };

  const calculateScore = () => {
    if (!aiContent?.data?.content?.questions || !quizState) return 0;
    
    let correct = 0;
    aiContent.data.content.questions.forEach(q => {
      if (quizState.answers[q.id] === q.correct_answer) {
        correct++;
      }
    });
    return correct;
  };

  const handleMarkComplete = async () => {
    if (!aiContent?.type || markingComplete) return;
    setMarkingComplete(true);
    try {
      await axios.post(`${API_URL}/api/progress/mark`, {
        lesson_id: lessonId,
        tool_type: aiContent.type
      }, { headers: getAuthHeaders() });
      setCompletedTools(prev => [...prev, aiContent.type]);
      toast.success('تم تسجيل إنجازك!');
    } catch {
      toast.error('حدث خطأ');
    } finally {
      setMarkingComplete(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!aiContent) return;
    
    setDownloadingPdf(true);
    
    try {
      const title = aiContent.title || 'محتوى تعليمي';
      const lessonTitle = lesson?.title_ar || '';
      
      let content = '';
      
      if (aiContent.type === 'quiz' && aiContent.data?.format === 'json' && aiContent.data?.content?.questions) {
        content = aiContent.data.content.quiz_title + '\n';
        content += 'الدرس: ' + lessonTitle + '\n';
        content += '═══════════════════════════════════════\n\n';
        
        aiContent.data.content.questions.forEach((q, idx) => {
          content += 'السؤال ' + (idx + 1) + ': ' + q.question + '\n\n';
          q.options.forEach((opt, oIdx) => {
            const letter = ['أ', 'ب', 'ج', 'د'][oIdx];
            content += '   ' + letter + ') ' + opt + '\n';
          });
          content += '\n';
        });
        
        content += '\n═══════════════════════════════════════\n';
        content += 'الإجابات الصحيحة:\n';
        aiContent.data.content.questions.forEach((q, idx) => {
          const letter = ['أ', 'ب', 'ج', 'د'][q.correct_answer];
          content += 'السؤال ' + (idx + 1) + ': ' + letter + '\n';
        });
      } else if (aiContent.type === 'worksheet' && aiContent.data?.format === 'json' && aiContent.data?.content?.sections) {
        const ws = aiContent.data.content;
        content = (ws.title || title) + '\n';
        content += 'الدرس: ' + lessonTitle + '\n';
        content += '═══════════════════════════════════════\n\n';
        
        ws.sections.forEach((section) => {
          content += '◆ ' + section.section_title + '\n';
          content += '───────────────────────────────\n';
          section.questions.forEach((q, qIdx) => {
            if (q.type === 'multiple_choice') {
              content += (qIdx + 1) + '. ' + q.question + '\n';
              q.options.forEach((opt, oIdx) => {
                content += '   ' + ['أ', 'ب', 'ج', 'د'][oIdx] + ') ' + opt + '\n';
              });
              content += '\n';
            } else if (q.type === 'fill_blank') {
              content += (qIdx + 1) + '. ' + q.question + '\n\n';
            } else if (q.type === 'true_false') {
              content += (qIdx + 1) + '. ' + q.question + '  (  صح  /  خطأ  )\n\n';
            } else if (q.type === 'match' && q.pairs) {
              content += (qIdx + 1) + '. وصّل بين العمود الأيمن والأيسر:\n\n';
              q.pairs.forEach((p, pIdx) => {
                content += '   ' + (pIdx + 1) + '. ' + p.right + '          ←          ' + p.left + '\n';
              });
              content += '\n';
            }
          });
          content += '\n';
        });
        
        content += '═══════════════════════════════════════\n';
        content += 'الإجابات الصحيحة:\n\n';
        ws.sections.forEach((section) => {
          content += '◆ ' + section.section_title + '\n';
          section.questions.forEach((q, qIdx) => {
            if (q.type === 'multiple_choice') {
              content += (qIdx + 1) + '. ' + ['أ', 'ب', 'ج', 'د'][q.correct_answer] + '\n';
            } else if (q.type === 'fill_blank') {
              content += (qIdx + 1) + '. ' + q.correct_answer + '\n';
            } else if (q.type === 'true_false') {
              content += (qIdx + 1) + '. ' + (q.correct_answer ? 'صح' : 'خطأ') + '\n';
            } else if (q.type === 'match' && q.pairs) {
              q.pairs.forEach((p, pIdx) => {
                content += '   ' + p.right + ' ← ' + p.left + '\n';
              });
            }
          });
          content += '\n';
        });
      } else {
        content = title + '\n';
        content += 'الدرس: ' + lessonTitle + '\n';
        content += '═══════════════════════════════════════\n\n';
        const raw = aiContent.data?.content;
        content += (typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2));
      }
      
      const blob = new Blob(['\ufeff' + content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = title + ' - ' + lessonTitle + '.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('تم تحميل الملف بنجاح!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('حدث خطأ أثناء إنشاء الملف');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePrintContent = () => {
    const title = aiContent?.title || 'محتوى تعليمي';
    const lessonTitle = lesson?.title_ar || '';
    
    let content = '';
    
    if (aiContent?.type === 'quiz' && aiContent?.data?.format === 'json' && aiContent?.data?.content?.questions) {
      content = `
        <div dir="rtl" style="font-family: 'Tajawal', Arial, sans-serif; padding: 40px; text-align: right;">
          <h1 style="text-align: center; margin-bottom: 20px;">${aiContent.data.content.quiz_title}</h1>
          <p style="text-align: center; margin-bottom: 30px;">الدرس: ${lessonTitle}</p>
          <hr style="margin-bottom: 30px;">
          ${aiContent.data.content.questions.map((q, idx) => `
            <div style="margin-bottom: 25px; padding: 15px; border: 1px solid #ddd; border-radius: 8px;">
              <p style="font-weight: bold; margin-bottom: 15px;">السؤال ${idx + 1}: ${q.question}</p>
              ${q.options.map((opt, oIdx) => `
                <p style="margin: 8px 0; padding-right: 20px;">${['أ', 'ب', 'ج', 'د'][oIdx]}) ${opt}</p>
              `).join('')}
            </div>
          `).join('')}
          <hr style="margin: 30px 0;">
          <h3>الإجابات الصحيحة:</h3>
          ${aiContent.data.content.questions.map((q, idx) => `
            <p>السؤال ${idx + 1}: ${['أ', 'ب', 'ج', 'د'][q.correct_answer]}</p>
          `).join('')}
        </div>
      `;
    } else if (aiContent?.type === 'worksheet' && aiContent?.data?.format === 'json' && aiContent?.data?.content?.sections) {
      const ws = aiContent.data.content;
      content = `
        <div dir="rtl" style="font-family: 'Tajawal', Arial, sans-serif; padding: 40px; text-align: right; line-height: 2;">
          <h1 style="text-align: center; margin-bottom: 10px;">${ws.title || title}</h1>
          <p style="text-align: center; margin-bottom: 30px; color: #666;">الدرس: ${lessonTitle}</p>
          <hr style="margin-bottom: 30px;">
          ${ws.sections.map(section => `
            <div style="margin-bottom: 30px;">
              <h2 style="color: #2563eb; margin-bottom: 15px; font-size: 18px;">${section.section_title}</h2>
              ${section.questions.map((q, qIdx) => {
                if (q.type === 'multiple_choice') {
                  return `
                    <div style="margin-bottom: 20px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px;">
                      <p style="font-weight: bold; margin-bottom: 10px;">${qIdx + 1}. ${q.question}</p>
                      ${q.options.map((opt, oIdx) => `
                        <p style="margin: 6px 0; padding-right: 20px;">○  ${['أ', 'ب', 'ج', 'د'][oIdx]}) ${opt}</p>
                      `).join('')}
                    </div>
                  `;
                } else if (q.type === 'fill_blank') {
                  return `
                    <div style="margin-bottom: 20px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px;">
                      <p style="font-weight: bold;">${qIdx + 1}. ${q.question}</p>
                      <div style="border-bottom: 2px dashed #ccc; width: 200px; margin-top: 10px; margin-right: 20px;">&nbsp;</div>
                    </div>
                  `;
                } else if (q.type === 'true_false') {
                  return `
                    <div style="margin-bottom: 20px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px;">
                      <p style="font-weight: bold;">${qIdx + 1}. ${q.question}</p>
                      <p style="margin-top: 8px; padding-right: 20px;">○ صح&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;○ خطأ</p>
                    </div>
                  `;
                } else if (q.type === 'match' && q.pairs) {
                  return `
                    <div style="margin-bottom: 20px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px;">
                      <p style="font-weight: bold; margin-bottom: 15px;">${qIdx + 1}. وصّل بين العمود الأيمن والأيسر:</p>
                      <table style="width: 100%; border-collapse: collapse;">
                        <thead>
                          <tr>
                            <th style="border: 1px solid #ddd; padding: 8px; background: #f9fafb; width: 45%;">العمود الأيمن</th>
                            <th style="border: 1px solid #ddd; padding: 8px; background: #f9fafb; width: 10%;"></th>
                            <th style="border: 1px solid #ddd; padding: 8px; background: #f9fafb; width: 45%;">العمود الأيسر</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${q.pairs.map((p, pIdx) => `
                            <tr>
                              <td style="border: 1px solid #ddd; padding: 10px; text-align: center;">${p.right}</td>
                              <td style="border: 1px solid #ddd; padding: 10px; text-align: center;">←</td>
                              <td style="border: 1px solid #ddd; padding: 10px; text-align: center;">${p.left}</td>
                            </tr>
                          `).join('')}
                        </tbody>
                      </table>
                    </div>
                  `;
                }
                return '';
              }).join('')}
            </div>
          `).join('')}
          <div style="page-break-before: always;"></div>
          <h2 style="margin-top: 30px;">الإجابات الصحيحة:</h2>
          <hr style="margin-bottom: 20px;">
          ${ws.sections.map(section => `
            <div style="margin-bottom: 15px;">
              <h3 style="color: #2563eb; font-size: 16px;">${section.section_title}</h3>
              ${section.questions.map((q, qIdx) => {
                if (q.type === 'multiple_choice') return `<p>${qIdx + 1}. ${['أ', 'ب', 'ج', 'د'][q.correct_answer]}) ${q.options[q.correct_answer]}</p>`;
                if (q.type === 'fill_blank') return `<p>${qIdx + 1}. ${q.correct_answer}</p>`;
                if (q.type === 'true_false') return `<p>${qIdx + 1}. ${q.correct_answer ? 'صح' : 'خطأ'}</p>`;
                if (q.type === 'match' && q.pairs) return q.pairs.map(p => `<p style="padding-right:20px;">${p.right} ← ${p.left}</p>`).join('');
                return '';
              }).join('')}
            </div>
          `).join('')}
        </div>
      `;
    } else {
      const textContent = aiContent?.data?.content || aiContent?.data?.solution || '';
      const displayContent = typeof textContent === 'string' ? textContent : JSON.stringify(textContent, null, 2);
      const formatted = contentRef.current?.querySelector('[data-testid="ai-text-content"]');
      content = `
        <div dir="rtl" style="font-family: 'Cairo', Tahoma, sans-serif; max-width: 780px; margin: auto; padding: 28px; text-align: right; line-height: 1.9;">
          <h1 style="text-align: center; margin-bottom: 20px;">${escapePrintText(title)}</h1>
          <p style="text-align: center; margin-bottom: 30px;">الدرس: ${escapePrintText(lessonTitle)}</p>
          <hr style="margin-bottom: 30px;">
          ${formatted?.outerHTML || `<div style="white-space: pre-wrap;">${escapePrintText(displayContent)}</div>`}
        </div>
      `;
    }
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('يرجى السماح بفتح نافذة الطباعة في المتصفح'); return; }
    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
        <head>
          <meta charset="UTF-8">
          <title>${escapePrintText(title)} - ${escapePrintText(lessonTitle)}</title>
          <link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700&display=swap" rel="stylesheet">
          <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800;900&display=swap" rel="stylesheet">
          <style>
            body { font-family: 'Tajawal', Arial, sans-serif; direction: rtl; }
            ${lessonReadingPrintStyles()}
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${content}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); }
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="lspage" dir="rtl">
        <header className="nav"><span className="brand">RasheedGPT</span></header>
        <main>
          <div className="hd"><h1>جارٍ التحميل...</h1></div>
        </main>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="lspage" dir="rtl">
        <header className="nav"><span className="brand">RasheedGPT</span></header>
        <main><div className="hd"><h1>الدرس غير موجود</h1></div></main>
      </div>
    );
  }

  return (
    <div className="lspage" dir="rtl">
      {/* Header */}
      <header className="nav no-print" data-testid="main-header">
        <Link to="/grades" className="brand" data-testid="logo-link">RasheedGPT</Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="who" data-testid="user-menu-trigger">
              <i aria-hidden="true">{user?.name?.charAt(0)}</i>
              <span className="hidden sm:inline">{user?.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48 bg-white border-black/10 text-[#1f1a33]">
            <div className="px-3 py-2 border-b border-black/5">
              <p className="font-semibold text-[#1f1a33]">{user?.name}</p>
              <p className="text-xs text-[#625c78]">{user?.role === 'admin' ? 'مدير' : user?.role === 'student' ? 'طالب' : 'ولي أمر'}</p>
            </div>
            {user?.role === 'admin' && (
              <DropdownMenuItem asChild className="text-[#4a3aa8] hover:text-[#4a3aa8] hover:bg-black/5 cursor-pointer">
                <Link to="/admin"><BarChart3 className="w-4 h-4 me-2" />لوحة التحكم</Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer">
              <Link to="/grades">الصفوف</Link>
            </DropdownMenuItem>
            {user?.role !== 'admin' && (
              <DropdownMenuItem asChild className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer">
                <Link to="/profile" data-testid="profile-link"><User className="w-4 h-4 me-2" />الملف الشخصي</Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setImageUploadOpen(true)} className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer" data-testid="solve-question-btn">
              <Camera className="w-4 h-4 me-2" />حل سؤال
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} className="text-red-500 hover:text-red-600 hover:bg-black/5 cursor-pointer" data-testid="logout-button">
              <LogOut className="w-4 h-4 me-2" />تسجيل الخروج
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="rings no-print" aria-hidden="true"><i></i><i></i><i></i></div>

      <main>
        <button className="back no-print" onClick={() => navigate(-1)} data-testid="back-button">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          العودة
        </button>

        <div className="hd">
          <h1 data-testid="lesson-title">{lesson.title_ar}</h1>
          {lesson.title_en && <p className="en">{lesson.title_en}</p>}
        </div>

        <div className="ly">
          <aside className="no-print">
            {lesson.pdf_url && (
              <div className="cd fl" data-testid="pdf-section">
                <div className="top">
                  <div className="ic"><FileText /></div>
                  <div><h2>ملف الدرس</h2><p>تحميل ملف PDF</p></div>
                </div>
                <button className="dl" onClick={() => window.open(lesson.pdf_url, '_blank')} data-testid="lesson-file-download-btn">
                  <Download />تحميل
                </button>
              </div>
            )}
            {lesson.content_ar && (
              <div className="cd ct" data-testid="lesson-content">
                <h2>محتوى الدرس</h2>
                <p>{lesson.content_ar}</p>
              </div>
            )}
            {gradeNumber && gradeNumber >= 1 && gradeNumber <= 9 && (
              <div className="cd pt" onClick={() => window.open(`https://elearn.edu.ps/landing3/c${gradeNumber}/`, '_blank')} data-testid="edu-portal-card">
                <div className="ic"><MonitorPlay /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2>البوابة التعليمية الفلسطينية</h2>
                  <p>المنصة التعليمية المعتمدة من وزارة التربية والتعليم العالي - شرح الدروس بالفيديو</p>
                </div>
                <ExternalLink className="ex" />
              </div>
            )}
            <div className="cd tip" data-testid="ai-parent-notice">
              <div className="ti"><Lightbulb /></div>
              <div>
                <h3>نصيحة مهمة لأولياء الأمور</h3>
                <p>المحتوى أدناه يتم إنشاؤه بواسطة الذكاء الاصطناعي وقد يحتوي على معلومات إضافية تتجاوز محتوى الدرس. للاستفادة المثلى من منصة رشيد، ننصحكم بقراءة الدرس وفهمه أولاً، ثم استخدام هذه الأدوات كوسيلة مساعدة.</p>
              </div>
            </div>
          </aside>

          <section aria-labelledby="ls-tt" className="no-print">
            <div className="th"><h2 id="ls-tt">أدوات الذكاء الاصطناعي</h2><p>اختر الأداة المناسبة، وكل أداة تعمل على محتوى هذا الدرس.</p></div>
            {(() => {
              const byId = (id) => aiFeatures.find(f => f.id === id);
              const feat = (id) => { const f = byId(id); return f ? () => handleAiFeature(f) : () => {}; };
              const ready = (id) => completedTools.includes(id) || cachedTypes.includes(id) || (id === 'teach-guide' && cachedGuideExists) || (id === 'explain-lesson' && cachedExplainExists) || (id === 'terms' && cachedTypes.includes('terms')) || (id === 'tips' && cachedTypes.includes('learning_tips'));
              const groups = [
                { k: 'learn', t: 'افهم الدرس', d: 'طرق مختلفة لشرح الدرس وتبسيطه', tools: [
                  { ic: <BookOpenCheck />, t: 'شرح الدرس للطالب', d: 'يعرض الدرس بأسلوب مبسّط وواضح يناسب عمر الطالب.', rdy: cachedExplainExists, on: feat('explain-lesson'), tid: 'ai-feature-explain-lesson' },
                  ...(showStoryFeature ? [{ ic: <BookOpenCheck />, t: 'اقرأ القصة المصورة', d: 'قصة مصورة تحكي فكرة الدرس، يمكنك قراءتها أو تحميلها كـ PDF.', rdy: cachedStoryExists, on: handleStoryGenerate, tid: 'ai-feature-story' }] : []),
                  ...(lesson?.video_url ? [{ ic: <MonitorPlay />, t: 'فيديو شرح الدرس', d: 'فيديو توضيحي قصير يشرح محتوى الدرس.', on: () => setVideoDialogOpen(true), tid: 'ai-feature-video' }] : []),
                  { ic: <BookOpen />, t: 'المصطلحات المهمة', d: 'أهم كلمات الدرس ومفاهيمه مع معنى كل منها.', rdy: cachedTypes.includes('terms'), on: feat('terms'), tid: 'ai-feature-terms' },
                ]},
                { k: 'practice', t: 'تدرّب واختبر نفسك', d: 'تمارين وأسئلة لتثبيت ما تعلّمته', tools: [
                  { ic: <ClipboardList />, t: 'ورقة عمل', d: 'تمارين متنوعة على الدرس يمكن حلّها وطباعتها.', on: feat('worksheet'), tid: 'ai-feature-worksheet' },
                  { ic: <HelpCircle />, t: 'اختبار قصير', d: 'أسئلة سريعة تقيس مدى فهمك للدرس.', on: feat('quiz'), tid: 'ai-feature-quiz' },
                  { ic: <Camera />, t: 'ساعدني في حل سؤال', d: 'صوّر السؤال الذي يواجهك واحصل على شرح لطريقة حله.', on: () => setImageUploadOpen(true), tid: 'ai-feature-help-solve' },
                ]},
                { k: 'ask', t: 'اسأل وتحاور', d: 'فكّر وحلّ خطوة بخطوة', tools: [
                  { ic: <MessageCircle />, t: 'اسأل عن محتوى الدرس', d: 'شارك سؤالك ومحاولتك، وأكمل الحل بنفسك مع تلميحات موجّهة.', on: () => setChatOpen(true), tid: 'ai-feature-chat' },
                  showDidTutor
                    ? { ic: <GraduationCap />, t: 'مدرسك الخصوصي', d: 'تحدّث مع مدرسك الخصوصي بالصوت والصورة وتدرّب على اللغة الإنجليزية.', on: () => setDidTutorOpen(true), tid: 'ai-feature-did-tutor', act: 'ابدأ المحادثة' }
                    : { ic: <GraduationCap />, t: 'مدرس خصوصي ذكي', d: 'مدرس يرافقك خطوة بخطوة ويجيب عن أسئلتك.', soon: true, tid: 'ai-feature-private-tutor' },
                ]},
                { k: 'teach', t: 'للمعلم وولي الأمر', d: 'مساعدة في شرح الدرس للأبناء والطلبة', tools: [
                  { ic: <GraduationCap />, t: 'دليل تدريس الدرس', d: 'خطة جاهزة لشرح الدرس خطوة بخطوة.', rdy: cachedGuideExists, on: feat('teach-guide'), tid: 'ai-feature-teach-guide' },
                  { ic: <Lightbulb />, t: 'تدريس محتوى الدرس', d: 'أفكار وأمثلة تساعدك على إيصال محتوى الدرس بسهولة.', rdy: cachedTypes.includes('learning_tips'), on: feat('tips'), tid: 'ai-feature-tips' },
                ]},
              ];
              const chips = [['all', 'الكل'], ...groups.map(g => [g.k, g.t])];
              return (
                <>
                  <div className="chips" role="group" aria-label="تصفية الأدوات">
                    {chips.map(([k, label]) => (
                      <button key={k} className="chip" aria-pressed={toolFilter === k} onClick={() => setToolFilter(k)} data-testid={`chip-${k}`}>{label}</button>
                    ))}
                  </div>
                  <div data-testid="ai-features">
                    {groups.filter(g => toolFilter === 'all' || toolFilter === g.k).map(g => (
                      <div className="gp" key={g.k}>
                        <div className="gh"><h3>{g.t}</h3><p>{g.d}</p></div>
                        <div className="tl">
                          {g.tools.map((tool, ti) => {
                            const isLoading = aiLoading && tool.tid === `ai-feature-${aiLoading}`;
                            return (
                              <div key={ti} className={`cd tool${tool.soon ? ' off' : ''}`} role="button" tabIndex={tool.soon ? -1 : 0}
                                onClick={tool.soon ? undefined : tool.on} data-testid={tool.tid}>
                                <div className="top">
                                  <div className="ic">{isLoading ? <Loader2 className="animate-spin" /> : tool.ic}</div>
                                  <h4>{tool.t}{tool.rdy && <span className="rd">جاهز</span>}{tool.soon && <span className="rd">قريباً</span>}</h4>
                                </div>
                                <p>{tool.d}</p>
                                <span className="act">{tool.soon ? 'قريباً' : tool.act ? tool.act : (tool.rdy ? 'عرض المحتوى' : 'اضغط للتوليد')}{!tool.soon && <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>}</span>
                              </div>
                            );
                          })}
                          {g.k === 'learn' && teacherVideos.map((tv, idx) => (
                            <div key={tv.id} className="cd tool" role="button" tabIndex={0} onClick={() => setTeacherVideoDialogOpen(tv)} data-testid={`teacher-video-${idx}`}>
                              <div className="top"><div className="ic"><MonitorPlay /></div><h4>شرح المعلم بالفيديو{teacherVideos.length > 1 ? ` (${idx + 1})` : ''}<span className="rd">المعلم</span></h4></div>
                              <p>فيديو من المعلم {tv.teacher_name}</p>
                              <span className="act">شاهد الفيديو<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg></span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </section>
        </div>

        {showDidTutor && <DidTutorWidget open={didTutorOpen} />}

        {/* Story Viewer */}
        <StoryViewer
          open={storyViewerOpen}
          onClose={() => { setStoryViewerOpen(false); }}
          story={storyData}
          lessonId={lessonId}
          isCompleted={completedTools.includes('story')}
          onMarkComplete={async () => {
            setMarkingComplete(true);
            try {
              await axios.post(`${API_URL}/api/progress/mark`, {
                lesson_id: lessonId,
                tool_type: 'story'
              }, { headers: getAuthHeaders() });
              setCompletedTools(prev => [...prev, 'story']);
              toast.success('تم تسجيل إنجازك!');
            } catch { toast.error('حدث خطأ'); }
            finally { setMarkingComplete(false); }
          }}
          markingComplete={markingComplete}
        />

        {/* Video Dialog */}
        {lesson?.video_url && (
          <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
            <DialogContent className="lesson-reader-dialog max-w-3xl bg-white border-black/10 text-[#1f1a33] p-0 overflow-hidden">
              <DialogDescription className="sr-only">فيديو شرح الدرس</DialogDescription>
              <div className="bg-red-500/10 p-5 pb-3">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold text-[#1f1a33] flex items-center gap-2">
                    <MonitorPlay className="w-5 h-5 text-red-400" />
                    فيديو شرح الدرس
                  </DialogTitle>
                </DialogHeader>
              </div>
              <div className="p-4">
                <video
                  controls
                  className="w-full rounded-xl"
                  src={lesson.video_url}
                  data-testid="lesson-video-player"
                >
                  متصفحك لا يدعم تشغيل الفيديو
                </video>
                <div className="mt-4">
                  {completedTools.includes('video') ? (
                    <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-emerald-500/15 border border-emerald-500/30" data-testid="video-mark-complete-done">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      <span className="text-emerald-400 font-semibold">تمت المشاهدة</span>
                    </div>
                  ) : (
                    <Button
                      onClick={async () => {
                        setMarkingComplete(true);
                        try {
                          await axios.post(`${API_URL}/api/progress/mark`, {
                            lesson_id: lessonId,
                            tool_type: 'video'
                          }, { headers: getAuthHeaders() });
                          setCompletedTools(prev => [...prev, 'video']);
                          toast.success('تم تسجيل إنجازك!');
                        } catch { toast.error('حدث خطأ'); }
                        finally { setMarkingComplete(false); }
                      }}
                      disabled={markingComplete}
                      className="w-full bg-black/[0.05] hover:bg-emerald-500/20 hover:border-emerald-500/40 border border-black/10 text-[#1f1a33]/80 hover:text-emerald-300 transition-all"
                      data-testid="video-mark-complete-btn"
                    >
                      {markingComplete ? (
                        <span className="animate-pulse">جارٍ الحفظ...</span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          تمت مشاهدة الفيديو
                        </span>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Teacher Video Dialog */}
        {teacherVideoDialogOpen && (
          <Dialog open={!!teacherVideoDialogOpen} onOpenChange={() => setTeacherVideoDialogOpen(null)}>
            <DialogContent className="lesson-reader-dialog max-w-2xl bg-white border-black/10 text-[#1f1a33]" dir="rtl">
              <DialogDescription className="sr-only">فيديو المعلم</DialogDescription>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-400">
                  <MonitorPlay className="w-5 h-5" />
                  شرح المعلم بالفيديو - {teacherVideoDialogOpen.teacher_name}
                </DialogTitle>
              </DialogHeader>
              <div>
                <video
                  controls
                  className="w-full rounded-xl"
                  src={teacherVideoDialogOpen.video_url}
                  data-testid="teacher-video-player"
                >
                </video>
                {teacherVideoDialogOpen.lesson_title && (
                  <p className="text-[#1f1a33]/40 text-xs mt-2">{teacherVideoDialogOpen.lesson_title}</p>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Lesson Chat */}
        <LessonChat
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          lessonId={lessonId}
          lessonTitle={lesson?.title_ar}
        />

        {/* AI Content Dialog */}
        {aiDialogOpen && <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
          <DialogContent className="lesson-reader-dialog p-0 overflow-hidden" dir="rtl" data-testid="lesson-ai-content-dialog">
            <DialogDescription className="sr-only">محتوى أداة الذكاء الاصطناعي</DialogDescription>
            <div className="lesson-reader-header">
              <DialogHeader>
                <DialogTitle className="lesson-reader-title" data-testid="lesson-ai-dialog-title">
                  {aiContent?.title}
                </DialogTitle>
                <p className="lesson-reader-subtitle" data-testid="lesson-ai-dialog-lesson">{lesson?.title_ar}</p>
              </DialogHeader>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handlePrintContent}
                      disabled={downloadingPdf}
                      className="text-[#1f1a33]/60 hover:text-[#1f1a33] hover:bg-black/[0.05]"
                      data-testid="download-pdf-btn"
                    >
                      <Download className="w-4 h-4 me-1" />
                      <span className="text-xs">تحميل PDF</span>
                    </Button>
            </div>
            
            <div className="lesson-reader-scroll" role="region" aria-label={aiContent?.title || 'محتوى الدرس'} tabIndex={0} data-testid="lesson-ai-content-scroll">
              <div dir="rtl" className="lesson-reader-body" ref={contentRef}>

              {/* Interactive Worksheet */}
              {aiContent?.type === 'worksheet' && aiContent?.data?.format === 'json' && aiContent?.data?.content?.sections ? (
                <InteractiveWorksheet theme="light" readingDesign data={aiContent.data.content} onScoreSubmit={(score, total, wrongQuestions) => {
                  axios.post(`${API_URL}/api/progress/score`, {
                    lesson_id: lessonId,
                    lesson_title: lesson?.title_ar || '',
                    tool_type: 'worksheet',
                    score, total,
                    wrong_questions: wrongQuestions || []
                  }, { headers: getAuthHeaders() }).catch(() => {});
                }} />
              ) :

              /* Quiz */
              aiContent?.type === 'quiz' && aiContent?.data?.format === 'json' && aiContent?.data?.content?.questions ? (
                <div className="lesson-exercise space-y-4" data-testid="quiz-content">
                  <h3 className="font-bold text-lg text-[#1f1a33] text-center" data-testid="quiz-title">
                    {aiContent.data.content.quiz_title}
                  </h3>
                  
                  {aiContent.data.content.questions.map((question, qIndex) => (
                    <div key={question.id} className="lesson-quiz-question" data-testid={`quiz-question-${question.id}`}>
                      <p className="font-semibold text-[#1f1a33] mb-3" data-testid={`quiz-question-text-${question.id}`}>
                        {qIndex + 1}. {question.question}
                      </p>
                      <div className="space-y-2">
                        {question.options.map((option, oIndex) => {
                          const isSelected = quizState?.answers[question.id] === oIndex;
                          const isSubmitted = quizState?.submitted;
                          const isCorrect = oIndex === question.correct_answer;
                          
                          let optionClass = 'lesson-quiz-option p-3 rounded-lg border-2 cursor-pointer ';
                          
                          if (isSubmitted) {
                            if (isCorrect) {
                              optionClass += 'border-green-500 bg-green-500/30 text-green-300';
                            } else if (isSelected) {
                              optionClass += 'border-red-500 bg-red-500/30 text-red-300';
                            } else {
                              optionClass += 'border-black/10 bg-black/[0.03] text-[#1f1a33]/50';
                            }
                          } else if (isSelected) {
                            optionClass += 'border-[#4a3aa8] bg-[#4a3aa8]/30 ring-2 ring-[#4a3aa8]/50 text-[#1f1a33]';
                          } else {
                            optionClass += 'border-black/10 bg-black/[0.03] text-[#1f1a33]/80 hover:border-black/20 hover:bg-black/[0.05]';
                          }
                          
                          return (
                            <button
                              key={oIndex}
                              type="button"
                              className={optionClass}
                              aria-pressed={isSelected}
                              data-result={isSubmitted && isCorrect ? 'correct' : isSubmitted && isSelected ? 'incorrect' : isSelected ? 'selected' : 'idle'}
                              onClick={() => handleQuizAnswer(question.id, oIndex)}
                              data-testid={`quiz-option-${question.id}-${oIndex}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                    isSubmitted && isCorrect ? 'border-green-400 bg-green-400' :
                                    isSubmitted && isSelected ? 'border-red-400 bg-red-400' :
                                    isSelected ? 'border-[#4a3aa8] bg-[#4a3aa8]' :
                                    'border-black/20'
                                  }`}>
                                    {(isSelected || (isSubmitted && isCorrect)) && (
                                      <div className="w-2 h-2 rounded-full bg-white" />
                                    )}
                                  </div>
                                  <span>{option}</span>
                                </div>
                                {isSubmitted && isCorrect && (
                                  <CheckCircle className="w-5 h-5 text-green-400" />
                                )}
                                {isSubmitted && isSelected && !isCorrect && (
                                  <XCircle className="w-5 h-5 text-red-400" />
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  
                  {!quizState?.submitted ? (
                    <Button 
                      className="w-full bg-[#4a3aa8] hover:bg-[#3b2d8c] text-[#1f1a33]"
                      onClick={submitQuiz}
                      disabled={Object.keys(quizState?.answers || {}).length < aiContent.data.content.questions.length}
                      data-testid="submit-quiz-btn"
                    >
                      إرسال الإجابات
                    </Button>
                  ) : (
                    <div className="text-center p-4 bg-[#4a3aa8]/20 rounded-xl" aria-live="polite" data-testid="quiz-score">
                      <p className="font-bold text-lg text-[#4a3aa8]">
                        نتيجتك: {calculateScore()} / {aiContent.data.content.questions.length}
                      </p>
                      <p className="text-sm text-[#1f1a33]/60 mt-1">
                        {calculateScore() === aiContent.data.content.questions.length 
                          ? 'ممتاز! أحسنت!' 
                          : calculateScore() >= aiContent.data.content.questions.length / 2 
                            ? 'جيد! استمر في التعلم' 
                            : 'حاول مرة أخرى'}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <LessonTextContent content={aiContent?.data?.content || aiContent?.data?.solution || ''} kind={aiContent?.type} />
              )}
              </div>

              {/* Mark as Complete Button */}
              {aiContent?.type && aiContent.type !== 'help-solve' && (
                <div className="lesson-reading-completion">
                  {completedTools.includes(aiContent.type) ? (
                    <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-emerald-500/15 border border-emerald-500/30" data-testid="mark-complete-done">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      <span className="text-emerald-400 font-semibold">تمت القراءة</span>
                    </div>
                  ) : (
                    <Button
                      onClick={handleMarkComplete}
                      disabled={markingComplete}
                      className="w-full bg-black/[0.05] hover:bg-emerald-500/20 hover:border-emerald-500/40 border border-black/10 text-[#1f1a33]/80 hover:text-emerald-300 transition-all"
                      data-testid="mark-complete-btn"
                    >
                      {markingComplete ? (
                        <span className="animate-pulse">جارٍ الحفظ...</span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          تم قراءتها
                        </span>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>}

        {/* Image Upload Dialog */}
        <Dialog open={imageUploadOpen} onOpenChange={(open) => { setImageUploadOpen(open); if (!open) { setSelectedImage(null); setImagePreview(null); } }}>
          <DialogContent className="lesson-reader-dialog max-w-md bg-white border-black/10 text-[#1f1a33]" data-testid="lesson-help-upload-dialog">
            <DialogDescription className="sr-only">رفع صورة لحل سؤال</DialogDescription>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#1f1a33] text-center">
                ساعدني في حل سؤال
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <p className="text-[#1f1a33]/60 text-sm text-center">
                ارفع صورة السؤال وسيساعدك الذكاء الاصطناعي بخطوات مرتبة لحله بنفسك
              </p>
              
              <input
                type="file"
                accept="image/*"
                ref={imageInputRef}
                className="hidden"
                onChange={handleImageSelect}
                data-testid="help-solve-file-input"
              />
              
              {imagePreview ? (
                <div className="space-y-3">
                  <div className="rounded-xl overflow-hidden border border-black/10">
                    <img src={imagePreview} alt="السؤال" className="w-full max-h-64 object-contain bg-black/[0.03]" />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 bg-[#4a3aa8] hover:bg-[#3b2d8c] text-[#1f1a33]"
                      onClick={handleImageSolve}
                      data-testid="help-solve-submit-btn"
                    >
                      <Lightbulb className="w-4 h-4 me-2" />
                      ساعدني في الحل
                    </Button>
                    <Button
                      variant="ghost"
                      className="text-[#1f1a33]/60 hover:text-[#1f1a33] hover:bg-black/[0.05]"
                      onClick={() => { setSelectedImage(null); setImagePreview(null); imageInputRef.current?.click(); }}
                    >
                      تغيير الصورة
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-black/10 rounded-xl p-8 text-center cursor-pointer hover:border-[#4a3aa8]/50 hover:bg-black/[0.03] transition-all"
                  onClick={() => imageInputRef.current?.click()}
                  data-testid="help-solve-upload-zone"
                >
                  <Camera className="w-12 h-12 mx-auto mb-3 text-[#1f1a33]/40" />
                  <p className="text-[#1f1a33]/60 font-medium">اضغط لرفع صورة السؤال</p>
                  <p className="text-[#1f1a33]/40 text-sm mt-1">PNG, JPG, JPEG</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default LessonDetailPage;